/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */

import { Evidence } from '@/@types/Evidence';
import { EvidenceCardContent } from '@/components/table/EvidenceCardContent';
import { PaperDetails } from '@/models/Report';
import { ZIndexEvidenceCard } from '@/constants/zIndex';

import { ClickAwayListener, Link, Typography } from '@mui/material';
import {
  Fragment,
  MouseEvent,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { v4 as uuidV4 } from 'uuid';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import Popper from '@mui/material/Popper';
import styled from 'styled-components';

type EvidenceCardPropsWithId = EvidenceCardBaseProps & {
  evidenceId: number;
};

type EvidenceCardPropsWithEvidence = EvidenceCardBaseProps & {
  evidences: Evidence[];
};

type EvidenceCardBaseProps = {
  onOpenOrClose?: (isOpen: boolean) => void;
  anchor?: ReactNode | null;
  corpusId?: number;
  paper?: PaperDetails;
};

// This component can either look up evidence if an id is provided
// or use existing evidence provided to it
export type EvidenceCardProps =
  | EvidenceCardPropsWithId
  | EvidenceCardPropsWithEvidence;

export enum EvidenceCardPlacement {
  bottom = 'bottom',
  topStart = 'top-start',
  left = 'left',
}

export const EvidenceCard = (props: EvidenceCardProps): ReactNode => {
  const [placement, setPlacement] = useState<EvidenceCardPlacement>(
    EvidenceCardPlacement.bottom,
  );

  const { onOpenOrClose, anchor, paper, corpusId, ...rest } = props;
  const ref = useRef<HTMLAnchorElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const id = isOpen ? `evidence-popover-${uuidV4()}` : undefined;

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    event.preventDefault();
    checkAvailableSpace();
    setIsOpen(true);
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  useEffect(() => {
    if (onOpenOrClose) {
      onOpenOrClose(isOpen);
    }
  }, [isOpen, onOpenOrClose]);

  const checkAvailableSpace = () => {
    if (ref.current) {
      const rect = ref.current.getBoundingClientRect();

      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceRight = window.innerWidth - rect.right;
      // If there is not enough space below, place the Popper at the top-left
      // I arrived on these numbers through experimentation locally.
      if (spaceRight < 500) {
        setPlacement(EvidenceCardPlacement.left);
      } else if (spaceBelow < 300) {
        setPlacement(EvidenceCardPlacement.topStart);
      } else {
        setPlacement(EvidenceCardPlacement.bottom);
      }
    }
  };

  const renderAuthors = useCallback(() => {
    const authors = paper?.authors || [];
    return authors.map((author, index) => (
      <Fragment key={author.authorId}>
        <AuthorLink
          href={`https://www.semanticscholar.org/author/${author.authorId}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          {author.name}
        </AuthorLink>
        {index < authors.length - 1 && <span>{', '}</span>}
      </Fragment>
    ));
  }, [paper?.authors]);

  const headerLabel = paper?.title || 'Evidence from Paper';
  return (
    <Container>
      <Anchor ref={ref} onClick={handleClick}>
        {anchor || <StyledFormatQuoteIcon />}
      </Anchor>
      <Popper
        id={id}
        style={{
          zIndex: ZIndexEvidenceCard,
        }}
        open={isOpen}
        anchorEl={ref.current}
        placement={placement}
        disablePortal={false}
        modifiers={[
          {
            name: 'flip',
            enabled: true,
            options: {
              altBoundary: true,
              boundary: 'document',
              padding: 8,
            },
          },
          {
            name: 'preventOverflow',
            enabled: true,
            options: {
              boundary: 'document',
              padding: 8,
            },
          },
        ]}
      >
        <ClickAwayListener onClickAway={handleClose}>
          <CardContainer>
            <Typography sx={{ mb: 0, mt: 0.5 }} variant="h6" color="secondary">
              {corpusId ? (
                <StyledLink
                  href={`https://www.semanticscholar.org/p/${corpusId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {headerLabel}
                </StyledLink>
              ) : (
                headerLabel
              )}
            </Typography>
            {paper && (
              <PaperMetadata>
                {paper?.authors && (
                  <MetadataItem>{renderAuthors()}</MetadataItem>
                )}
                {paper?.venue && <MetadataItem>{paper.venue}</MetadataItem>}
                {paper?.year && <MetadataItem>{paper.year}</MetadataItem>}
                {!!paper?.nCitations && (
                  <MetadataItem>
                    {paper.nCitations}{' '}
                    {paper.nCitations > 1 ? 'citations' : 'citation'}
                  </MetadataItem>
                )}
              </PaperMetadata>
            )}

            {isOpen && <EvidenceCardContent {...rest} />}
          </CardContainer>
        </ClickAwayListener>
      </Popper>
    </Container>
  );
};

const CardContainer = styled('div')(({ theme }) => ({
  maxWidth: 600,
  maxHeight: 300,
  overflow: 'auto',
  backgroundColor: '#08232b',
  color: '#FAF2E9',
  padding: '12px 16px 4px 16px',

  [theme.breakpoints.up('md')]: {
    padding: '16px 24px 12px 24px',
  },
}));

const Container = styled.div`
  display: inline-block;
`;

const StyledFormatQuoteIcon = styled(FormatQuoteIcon)`
  color: ${({ theme }) => theme.palette.primary.main};
  font-size: 0.7em;
  vertical-align: top;
  margin-left: 4px;
`;

const Anchor = styled('span')`
  cursor: pointer;
`;

const StyledLink = styled(Link)`
  &.MuiTypography-root {
    font-weight: bold;
    color: ${({ theme }) => theme.palette.secondary.main};
    &:hover {
      color: ${({ theme }) => theme.palette.secondary.light};
    }
  }
`;

const AuthorLink = styled(Link)`
  &.MuiTypography-root {
    color: ${({ theme }) => theme.palette.primary.main};
    &:hover {
      color: ${({ theme }) => theme.palette.primary.light};
    }
  }
`;

export const PaperMetadata = styled.ul`
  color: ${({ theme }) => theme.color['cream-100'].hex};
  list-style-type: none;
  padding: 0;
  text-wrap: wrap;
`;

export const MetadataItem = styled.li`
  display: inline;
  margin-right: ${({ theme }) => theme.spacing(1)};
  &:nth-of-type(n + 2):before {
    content: '•';
    margin-right: ${({ theme }) => theme.spacing(1)};
  }
`;
